import { Link } from "@tanstack/react-router";
import { formatDate } from "@/lib/format";
import { StatusBadge } from "@/components/ui/status-badge";
import { Img } from "@/components/ui/img";
import { GeneratedCover } from "@/components/blog/GeneratedCover";
import { AuthorAvatar } from "@/components/blog/AuthorAvatar";
import { readingMinutes } from "@/lib/reading-time";
import { isBlogCategory, BLOG_CATEGORY_LABELS } from "@/lib/blog-categories";
import { blogCardExcerpt } from "@/lib/blog-excerpt";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content?: string;
  readingMinutes?: number;
  category?: string | null;
  coverImageUrl?: string | null;
  authorName?: string | null;
  authorImage?: string | null;
  status: string;
  publishedAt?: string | null;
  createdAt: string;
}

interface BlogCardProps {
  post: BlogPost;
  showStatus?: boolean;
  editHref?: string;
  hideAuthor?: boolean;
}

export function BlogCard({ post, showStatus, editHref, hideAuthor }: BlogCardProps) {
  const linkTo = editHref || `/blog/${post.slug}`;
  const excerpt = blogCardExcerpt(post);
  const minutes = post.readingMinutes ?? (post.content ? readingMinutes(post.content) : null);

  return (
    <Link
      to={linkTo}
      className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card transition-shadow hover:shadow-md"
    >
      {/* Cover image */}
      <div className="aspect-[16/9] overflow-hidden">
        {post.coverImageUrl ? (
          <Img
            src={post.coverImageUrl}
            alt={post.title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <GeneratedCover
            slug={post.slug}
            category={post.category}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        )}
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col p-5">
        {/* Meta row */}
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {isBlogCategory(post.category) && (
            <span className="font-semibold uppercase tracking-[0.08em] text-primary">
              {BLOG_CATEGORY_LABELS[post.category]}
            </span>
          )}
          <time>{formatDate(post.publishedAt || post.createdAt)}</time>
          {minutes !== null && (
            <>
              <span aria-hidden>·</span>
              <span>{minutes} min čitanja</span>
            </>
          )}
          {showStatus && (
            <StatusBadge status={post.status} />
          )}
        </div>

        {/* Title */}
        <h3
          className="mt-2 text-lg font-semibold leading-snug text-foreground group-hover:text-primary transition-colors line-clamp-2"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {post.title}
        </h3>

        {/* Excerpt */}
        {excerpt && (
          <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground line-clamp-3">
            {excerpt}
          </p>
        )}

        {/* Author */}
        {!hideAuthor && post.authorName && (
          <div className="mt-4 flex items-center gap-2">
            <AuthorAvatar name={post.authorName} image={post.authorImage} size={24} />
            <span className="text-xs font-medium text-foreground/70">
              {post.authorName}
            </span>
          </div>
        )}
      </div>
    </Link>
  );
}
